/* eslint-disable */
import * as React from 'react';
import useLatest from '@react-hook/latest';
import useEvent from '@react-hook/event';
import { useThrottleCallback } from '@react-hook/throttle';
import { animateScroll } from 'react-scroll';

const defaultState = {
    index: undefined,
    position: undefined,
    prevTop: undefined
};

export function useScrollToIndex(
    positioner,
    options
) {
    const {
        align = 'top',
        element = typeof window !== 'undefined' && window,
        offset = 0,
        height = typeof window !== 'undefined' ? window.innerHeight : 0
    } = options;

    const latestOptions = useLatest({
        positioner,
        element,
        align,
        offset,
        height
    });

    const getTarget = React.useRef(() => {
        const latestElement = latestOptions.current.element;

        return latestElement && 'current' in latestElement
            ? latestElement.current
            : latestElement;
    }).current;

    const [state, dispatch] = React.useReducer(
        (
            // eslint-disable-next-line no-shadow
            state,
            action
        ) => {
            const nextState = {
                position: state.position,
                index: state.index,
                prevTop: state.prevTop,
            };

            if (action.type === 'scrollToIndex') {
                return {
                    position: latestOptions.current.positioner.get(action.value ?? -1),
                    index: action.value,
                    prevTop: undefined
                };
            } else if (action.type === 'setPosition') {
                nextState.position = action.value;
            } else if (action.type === 'setPrevTop') {
                nextState.prevTop = action.value;
            } else if (action.type === 'reset') {
                return defaultState;
            }

            return nextState;
        },
        defaultState
    );

    const throttledDispatch = useThrottleCallback(dispatch, 15);

    useEvent(getTarget(), 'scroll', () => {
        if (!state.position && state.index) {
            const position = latestOptions.current.positioner.get(state.index);

            if (position) {
                dispatch({ type: 'setPosition', value: position });
            }
        }
    });

    const currentTop = state.index !== undefined && latestOptions.current.positioner.get(state.index)?.top;

    React.useEffect(() => {
        const target = getTarget();

        if (!target) {
            return;
        }

        // eslint-disable-next-line no-shadow
        const { height, align, offset, positioner } = latestOptions.current;

        if (state.position) {
            let scrollTop = state.position.top;

            if (align === 'bottom') {
                scrollTop = scrollTop - height + state.position.height;
            } else if (align === 'center') {
                scrollTop -= (height - state.position.height) / 2;
            }

            animateScroll.scrollTo(Math.max(0, (scrollTop += offset)), { duration: 500 });

            let didUnsubscribe = false;
            const timeout = setTimeout(
                () => !didUnsubscribe && dispatch({ type: 'reset' }),
                600
            );

            return () => {
                didUnsubscribe = true;
                clearTimeout(timeout);
            };
        } else if (state.index !== undefined) {
            let estimatedTop = (positioner.shortestColumn() / positioner.size()) * state.index;

            if (state.prevTop) {
                estimatedTop = Math.max(estimatedTop, state.prevTop + height);
            }

            animateScroll.scrollTo(estimatedTop, { duration: 500 });
            throttledDispatch({ type: 'setPrevTop', value: estimatedTop });
        }
    }, [currentTop, state, latestOptions, getTarget, throttledDispatch]);

    return React.useRef((index) => {
        dispatch({ type: 'scrollToIndex', value: index });
    }).current;
}
